import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { waitlistAPI } from '../../../services/api';
import { Search, Filter, Trash2, ArrowRightCircle, ListOrdered } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const BookingWaitlist = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();

    const [waitlist, setWaitlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchWaitlist();
    }, []);

    const fetchWaitlist = async () => {
        try {
            setLoading(true);
            const response = await waitlistAPI.getAll();
            setWaitlist(response.waitlist || []);
        } catch (error) {
            console.error('Error fetching waitlist:', error);
            toast.error(t('error_loading_data'));
        } finally {
            setLoading(false);
        }
    };

    const handleProcess = async (id) => {
        try {
            await waitlistAPI.process(id);
            toast.success(t('waitlist_processed'));
            fetchWaitlist();
        } catch (error) {
            console.error('Process error:', error);
            toast.error(error.response?.data?.message || t('failed_process_waitlist'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('remove_waitlist_confirm'))) return;
        try {
            await waitlistAPI.remove(id);
            toast.success(t('entry_removed'));
            fetchWaitlist();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(t('failed_remove_entry'));
        }
    };

    const filtered = waitlist.filter(w =>
        w.prospectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        w.prospectPhone?.includes(searchTerm) ||
        w.apartmentNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <div className="flex items-center gap-2 text-sm mb-2">
                        <Link to="/admin/bookings" className={`${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>{t('bookings')}</Link>
                        <span className={isDarkMode ? 'text-zinc-600' : 'text-gray-300'}>/</span>
                        <span className={isDarkMode ? 'text-zinc-300' : 'text-gray-700'}>{t('waitlist')}</span>
                    </div>
                    <h1 className={`text-2xl font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <ListOrdered className="mr-3" />
                        {t('waitlist_management')}
                    </h1>
                </div>
            </div>

            <div className={`p-4 rounded-xl border flex gap-4 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                <div className="relative w-full max-w-md">
                    <Search className={`absolute start-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`} size={18} />
                    <input
                        type="text"
                        placeholder={t('search_waitlist')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full ps-10 pe-4 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-red-500 outline-none transition-colors ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
            ) : filtered.length === 0 ? (
                <div className={`text-center py-16 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <ListOrdered size={48} className={`mx-auto mb-4 ${isDarkMode ? 'text-zinc-700' : 'text-gray-300'}`} />
                    <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('waitlist_empty')}</h3>
                    <p className={`mt-1 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                        {t('no_prospective_tenants_waiting')}
                    </p>
                </div>
            ) : (
                <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-gray-200 bg-white'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-start text-sm">
                            <thead className={`text-xs uppercase ${isDarkMode ? 'bg-zinc-800/50 text-zinc-400' : 'bg-gray-50 text-gray-500'}`}>
                                <tr>
                                    <th className="px-6 py-4 font-medium">{t('priority')}</th>
                                    <th className="px-6 py-4 font-medium">{t('prospect')}</th>
                                    <th className="px-6 py-4 font-medium">{t('desired_unit')}</th>
                                    <th className="px-6 py-4 font-medium">{t('move_in_preference')}</th>
                                    <th className="px-6 py-4 font-medium">{t('added')}</th>
                                    <th className="px-6 py-4 font-medium text-end">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-zinc-800' : 'divide-gray-100'}`}>
                                {filtered.map((entry) => (
                                    <tr key={entry.id} className={`group transition-colors ${isDarkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-50'}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2.5 py-1 rounded text-xs font-semibold ${entry.priority > 0 ? (isDarkMode ? 'bg-red-900/30 text-red-500' : 'bg-red-100 text-red-700') : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-600')}`}>
                                                {t('rank', { priority: entry.priority })}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {entry.prospectName}
                                            </div>
                                            <div className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                                {entry.prospectPhone}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`font-medium ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                                                {t('apartment_display', { number: entry.apartmentNumber })}
                                            </div>
                                            <div className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                                                {entry.buildingName} • {t('floor_display', { number: entry.floor })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={isDarkMode ? 'text-zinc-300' : 'text-gray-700'}>
                                                {new Date(entry.preferredMoveIn).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'}>
                                                {new Date(entry.createdAt).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-end">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleProcess(entry.id)}
                                                    title={t('process_to_booking')}
                                                    className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'text-green-500 hover:bg-green-500/10' : 'text-green-600 hover:bg-green-50'}`}
                                                >
                                                    <ArrowRightCircle size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(entry.id)}
                                                    title={t('remove')}
                                                    className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'text-red-500 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'}`}
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingWaitlist;
